import { useEffect, useState } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { localizeShots } from './shots';
import { SITE_TRANSLATIONS } from './siteContentI18n';

/*
 * Default marketing-site content, taken 1:1 from the Figma "Home" frame.
 * Everything here can be overridden per language in Admin → Landingspagina.
 * Wrap words in *asterisks* to render them in the brand green.
 * Languages without their own defaults fall back to English.
 */
const NL = {
  topbar: {
    items: ['Geen setup kosten', 'Cancel elk moment', 'GDPR-conform'],
    faq_label: 'FAQ',
  },
  nav: {
    links: [
      { label: 'Home', to: '/' },
      { label: 'Functies', to: '/functionaliteiten' },
      { label: 'Hoe het werkt', to: '/hoe-het-werkt' },
      { label: 'Prijzen', to: '/pricing' },
      { label: 'Over ons', to: '/over-ons' },
      { label: 'Contact', to: '/contact' },
    ],
    login_label: 'Login',
    cta_label: 'Start gratis',
    dashboard_label: 'Naar dashboard',
  },
  hero: {
    kicker: '30 dagen gratis proberen',
    title: '*Meet je winst* per WooCommerce shop',
    subtitle: 'Verbind je WooCommerce-shop en Google Ads in 2 minuten en zie meteen waar je webshop écht op verdient.',
    cta_primary: 'Start gratis',
    cta_secondary: 'View demo',
    cta_secondary_link: '/hoe-het-werkt',
    image_url: '/brand/hero-screen.png',
    stats: [
      { value: '2.400*+*', label: 'Gesynchroniseerde bestellingen per dag' },
      { value: '*<* 10 min', label: 'Gemiddelde setuptijd' },
      { value: '99,9*%*', label: 'Platform uptime' },
      { value: 'EU', label: 'Gehost, GDPR-conform' },
    ],
  },
  intro: {
    title: '*Begrijp* uw werkelijke winst',
  },
  blocks: [
    {
      kicker: 'Het probleem',
      title: 'WooCommerce toont je omzet. Niet je winst.',
      body: 'Tussen inkoopprijzen, verzendkosten, advertentiebudgetten en terugbetalingen raakt het antwoord op “verdien ik hier echt aan?” al snel verloren in losse spreadsheets en tabs.\nWowProfit.io brengt al die cijfers automatisch samen tot één helder getal: je échte nettowinst, per product, per land en per maand.',
      cta_label: 'Start gratis',
      cta_link: 'register',
      image_url: '/brand/shot-financieel.png',
    },
    {
      kicker: 'Verkopen',
      title: 'Elke bestelling, direct met marge',
      body: 'Zie per bestelling wat er binnenkomt en wat je eraan overhoudt — inclusief verzendkosten, betaalprovider-fees en advertentie-toewijzing. Geen handmatig rekenwerk meer.',
      cta_label: 'Start gratis',
      cta_link: 'register',
      image_url: '/brand/shot-verkopen.png',
    },
    {
      kicker: 'Hoe het werkt',
      title: 'Van shop-koppeling tot winstinzicht in 4 stappen',
      body: 'Koppel WowProfit aan je webshop, voer je kosten in en krijg een helder beeld van je werkelijke winst — alles op één plek.',
      cta_label: 'Ontdek WowProfit',
      cta_link: '/hoe-het-werkt',
      image_url: '/brand/shot-producten.png',
    },
  ],
  features: {
    kicker: 'Functionaliteiten',
    title: 'Alles wat je nodig hebt voor winstoptimalisatie',
    body: 'Van automatische rapportage tot AI-powered inzichten. WowProfit.io helpt je elke euro te tracken.',
    cta_label: 'Bekijk alle functies',
    cta_link: '/functionaliteiten',
    items: [
      { icon: 'chart', title: 'Automatische Rapportage', description: 'Dagelijkse, maandelijkse en jaarlijkse winst berekeningen. Export naar Google Sheets of CSV.' },
      { icon: 'globe', title: 'Per Country Analysis', description: 'Zie per land je omzet, btw en nettowinst, zodat je weet in welke markten je écht verdient.' },
      { icon: 'coin', title: 'Cost Price Tracking', description: 'Leg inkoopprijzen per product vast en zie direct je marge per product en per bestelling.' },
      { icon: 'bolt', title: 'AI-Powered Insights', description: 'Onze AI signaleert dalende marges en producten voordat ze je omzet raken.' },
      { icon: 'team', title: 'Team Collaboration', description: 'Werk samen met je team en boekhouder in één gedeeld dashboard.' },
    ],
  },
  radar: {
    kicker: 'Price radar',
    title: 'Smart *recommendations*, no dashboard digging',
    body: 'Our AI detects margin declines, declining products and price differences with competitors before they affect your revenue.\nPrice development per competitor, measured at every daily check.',
    image_url: '/brand/shot-prijsradar.png',
  },
  audience: {
    kicker: 'Voor wie',
    title: '*Gebouwd* voor elke schaal van webshop',
    items: [
      { title: 'Solo-*ondernemers*', text: 'Één shop, één persoon, geen tijd voor spreadsheets. Zie in 10 seconden of je deze maand winst maakt.' },
      { title: '*Multi-shop* retailers', text: 'Beheer meerdere WooCommerce-winkels vanuit één dashboard en vergelijk hun winstgevendheid direct.' },
      { title: '*Performance* marketeers', text: 'Koppel advertentie-uitgaven aan écht nettoresultaat in plaats van alleen omzet of ROAS.' },
    ],
  },
  pricing: {
    kicker: 'Prijzen',
    title: '*Transparante* prijzen, geen verborgen kosten',
    note: 'Alle prijzen zijn exclusief BTW. Start met 30 dagen gratis proberen.',
    monthly_label: 'Maandelijks',
    yearly_label: 'Jaarlijks',
    per_month: 'per maand',
    per_year: 'per jaar',
    all_plans_label: 'Bekijk alle abonnementen',
  },
  cta: {
    title: 'Weet vandaag nog wat je écht verdient',
    subtitle: 'Gratis proberen, geen kaartgegevens nodig.',
    primary_label: 'Start gratis',
    secondary_label: 'Praat met het team',
    secondary_link: '/contact',
  },
  footer: {
    description: 'Het financiële commandocentrum voor WooCommerce-ondernemers. Winst, kosten en groei in real-time, in één helder dashboard.',
    location: 'West-Vlaanderen, België · BTW BE0793944703',
    columns: [
      {
        title: 'Product',
        links: [
          { label: 'Home', to: '/' },
          { label: 'Functies', to: '/functionaliteiten' },
          { label: 'Hoe het werkt', to: '/hoe-het-werkt' },
          { label: 'Prijzen', to: '/pricing' },
        ],
      },
      {
        title: 'Bedrijf',
        links: [
          { label: 'Over ons', to: '/over-ons' },
          { label: 'Contact', to: '/contact' },
          { label: 'info@wowprofit.io', to: 'mailto:info@wowprofit.io' },
          { label: 'FAQ', to: '/faq' },
        ],
      },
      {
        title: 'Juridisch',
        links: [
          { label: 'Privacybeleid', to: '/privacy-beleid' },
          { label: 'Algemene voorwaarden', to: '/algemene-voorwaarden' },
          { label: 'Cookies', to: '/cookies' },
          { label: 'Sitemap', to: 'sitemap' },
        ],
      },
    ],
    copyright: '© {year} Webtron BV — WowProfit.io. Alle rechten voorbehouden.',
  },
};

const EN = {
  topbar: {
    items: ['No setup costs', 'Cancel anytime', 'GDPR compliant'],
    faq_label: 'FAQ',
  },
  nav: {
    links: [
      { label: 'Home', to: '/' },
      { label: 'Features', to: '/functionaliteiten' },
      { label: 'How it works', to: '/hoe-het-werkt' },
      { label: 'Pricing', to: '/pricing' },
      { label: 'About us', to: '/over-ons' },
      { label: 'Contact', to: '/contact' },
    ],
    login_label: 'Login',
    cta_label: 'Start free',
    dashboard_label: 'To dashboard',
  },
  hero: {
    kicker: 'Try free for 30 days',
    title: '*Measure your profit* per WooCommerce shop',
    subtitle: 'Connect your WooCommerce shop and Google Ads in 2 minutes and instantly see where your store really makes money.',
    cta_primary: 'Start free',
    cta_secondary: 'View demo',
    cta_secondary_link: '/hoe-het-werkt',
    image_url: '/brand/hero-screen.png',
    stats: [
      { value: '2,400*+*', label: 'Orders synchronised per day' },
      { value: '*<* 10 min', label: 'Average setup time' },
      { value: '99.9*%*', label: 'Platform uptime' },
      { value: 'EU', label: 'Hosted, GDPR compliant' },
    ],
  },
  intro: {
    title: '*Understand* your real profit',
  },
  blocks: [
    {
      kicker: 'The problem',
      title: 'WooCommerce shows your revenue. Not your profit.',
      body: 'Between purchase prices, shipping costs, ad budgets and refunds, the answer to “am I really making money on this?” quickly gets lost in scattered spreadsheets and tabs.\nWowProfit.io automatically brings all those numbers together into one clear figure: your real net profit, per product, per country and per month.',
      cta_label: 'Start free',
      cta_link: 'register',
      image_url: '/brand/shot-financieel.png',
    },
    {
      kicker: 'Sales',
      title: 'Every order, with its margin',
      body: 'See for every order what comes in and what you keep — including shipping, payment provider fees and ad attribution. No more manual calculations.',
      cta_label: 'Start free',
      cta_link: 'register',
      image_url: '/brand/shot-verkopen.png',
    },
    {
      kicker: 'How it works',
      title: 'From shop connection to profit insight in 4 steps',
      body: 'Connect WowProfit to your store, enter your costs and get a clear view of your real profit — all in one place.',
      cta_label: 'Discover WowProfit',
      cta_link: '/hoe-het-werkt',
      image_url: '/brand/shot-producten.png',
    },
  ],
  features: {
    kicker: 'Features',
    title: 'Everything you need to optimise profit',
    body: 'From automatic reporting to AI-powered insights. WowProfit.io helps you track every euro.',
    cta_label: 'See all features',
    cta_link: '/functionaliteiten',
    items: [
      { icon: 'chart', title: 'Automatic Reporting', description: 'Daily, monthly and yearly profit calculations. Export to Google Sheets or CSV.' },
      { icon: 'globe', title: 'Per Country Analysis', description: 'See revenue, VAT and net profit per country, so you know which markets really pay off.' },
      { icon: 'coin', title: 'Cost Price Tracking', description: 'Record purchase prices per product and instantly see your margin per product and order.' },
      { icon: 'bolt', title: 'AI-Powered Insights', description: 'Our AI flags falling margins and products before they hit your revenue.' },
      { icon: 'team', title: 'Team Collaboration', description: 'Work with your team and accountant in one shared dashboard.' },
    ],
  },
  radar: {
    kicker: 'Price radar',
    title: 'Smart *recommendations*, no dashboard digging',
    body: 'Our AI detects margin declines, declining products and price differences with competitors before they affect your revenue.\nPrice development per competitor, measured at every daily check.',
    image_url: '/brand/shot-prijsradar.png',
  },
  audience: {
    kicker: 'Who it’s for',
    title: '*Built* for every size of web shop',
    items: [
      { title: 'Solo *entrepreneurs*', text: 'One shop, one person, no time for spreadsheets. See in 10 seconds whether you’re profitable this month.' },
      { title: '*Multi-shop* retailers', text: 'Manage several WooCommerce stores from one dashboard and compare their profitability instantly.' },
      { title: '*Performance* marketers', text: 'Link ad spend to real net results instead of just revenue or ROAS.' },
    ],
  },
  pricing: {
    kicker: 'Pricing',
    title: '*Transparent* pricing, no hidden costs',
    note: 'All prices exclude VAT. Start with a 30-day free trial.',
    monthly_label: 'Monthly',
    yearly_label: 'Yearly',
    per_month: 'per month',
    per_year: 'per year',
    all_plans_label: 'See all plans',
  },
  cta: {
    title: 'Know today what you really earn',
    subtitle: 'Try it free, no card details needed.',
    primary_label: 'Start free',
    secondary_label: 'Talk to the team',
    secondary_link: '/contact',
  },
  footer: {
    description: 'The financial command centre for WooCommerce merchants. Profit, costs and growth in real time, in one clear dashboard.',
    location: 'West Flanders, Belgium · VAT BE0793944703',
    columns: [
      {
        title: 'Product',
        links: [
          { label: 'Home', to: '/' },
          { label: 'Features', to: '/functionaliteiten' },
          { label: 'How it works', to: '/hoe-het-werkt' },
          { label: 'Pricing', to: '/pricing' },
        ],
      },
      {
        title: 'Company',
        links: [
          { label: 'About us', to: '/over-ons' },
          { label: 'Contact', to: '/contact' },
          { label: 'info@wowprofit.io', to: 'mailto:info@wowprofit.io' },
          { label: 'FAQ', to: '/faq' },
        ],
      },
      {
        title: 'Legal',
        links: [
          { label: 'Privacy Policy', to: '/privacy-beleid' },
          { label: 'Terms of Service', to: '/algemene-voorwaarden' },
          { label: 'Cookies', to: '/cookies' },
          { label: 'Sitemap', to: 'sitemap' },
        ],
      },
    ],
    copyright: '© {year} Webtron BV — WowProfit.io. All rights reserved.',
  },
};

export const SITE_DEFAULTS = { nl: NL, en: EN };

/* nl/en have full defaults; fr/de/es/zh are English with their translated blocks on top. */
export const defaultsFor = (lang) => {
  if (SITE_DEFAULTS[lang]) return SITE_DEFAULTS[lang];
  const tr = SITE_TRANSLATIONS[lang];
  return tr ? mergeContent(EN, tr) : EN;
};

const isPlainObject = (v) => v && typeof v === 'object' && !Array.isArray(v);

/* Admin values win; empty strings and empty arrays fall back to the defaults. */
export const mergeContent = (base, override) => {
  if (override === undefined || override === null || override === '') return base;
  if (Array.isArray(base)) {
    if (!Array.isArray(override) || override.length === 0) return base;
    return override.map((item, i) => (base[i] !== undefined ? mergeContent(base[i], item) : item));
  }
  if (isPlainObject(base)) {
    if (!isPlainObject(override)) return base;
    const out = { ...base };
    Object.keys(override).forEach((k) => {
      out[k] = k in base ? mergeContent(base[k], override[k]) : override[k];
    });
    return out;
  }
  return override;
};

const cache = {};

const fetchCms = (lang) => {
  if (!cache[lang]) {
    cache[lang] = axios
      .get(`/landing-content?lang=${lang}`)
      .then((res) => res.data?.content || {})
      .catch(() => {
        delete cache[lang];
        return {};
      });
  }
  return cache[lang];
};

/* Resolved marketing content for the current UI language (defaults + admin overrides). */
export const useSiteContent = () => {
  const { i18n } = useTranslation();
  const lang = (i18n.language || 'nl').slice(0, 2);
  const [cms, setCms] = useState({});

  useEffect(() => {
    let alive = true;
    fetchCms(lang).then((c) => alive && setCms(c));
    return () => {
      alive = false;
    };
  }, [lang]);

  return { lang, cms, content: localizeShots(mergeContent(defaultsFor(lang), cms), lang) };
};
